import * as React from 'react';

/**
 * Seo Component
 * 
 * Provides consistent metadata across all pages and templates.
 * 
 * @example
 * // In a page's Head export:
 * export const Head = () => (
 *   <Seo 
 *     title="About Me" 
 *     description="Learn more about Jasen Wyatt"
 *     pathname="/about"
 *   />
 * )
 * 
 * @example
 * // In a template with dynamic data:
 * export const Head = ({ data }) => (
 *   <Seo
 *     title={data.wpPost.seo?.title || data.wpPost.title}
 *     description={data.wpPost.seo?.metaDesc}
 *     pathname={data.wpPost.uri}
 *     image={data.wpPost.featuredImage?.node?.sourceUrl}
 *     article={true}
 *   />
 * )
 */

const Seo = ({
  title,
  description,
  pathname = '/',
  image,
  article = false,
  noindex = false,
}) => {
  // Site constants
  const siteUrl = 'https://jasen.dev';
  const siteName = 'Jasen Wyatt';
  const defaultTitle = 'Jasen Wyatt | UX Designer & Web Developer';
  const defaultDescription = 'Jasen Wyatt is a UX Designer and Web Developer based out of Metro Detroit. He is a generalist with deep expertise in interaction design and full-stack development, focused on creating fast, accessible, and user-centered digital experiences.';
  const defaultImage = `${siteUrl}/og-image.png`; // You can add a default OG image to the static folder

  // Computed values
  const pageTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const metaDescription = description || defaultDescription;
  const canonicalUrl = `${siteUrl}${pathname}`;
  const ogImage = image || defaultImage;
  const ogType = article ? 'article' : 'website';

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Favicons */}
      <link rel="icon" type="image/x-icon" href="/favicon/favicon.ico" />
      <link rel="apple-touch-icon-precomposed" sizes="57x57" href="/favicon/apple-touch-icon-57x57.png" />
      <link rel="apple-touch-icon-precomposed" sizes="114x114" href="/favicon/apple-touch-icon-114x114.png" />
      <link rel="apple-touch-icon-precomposed" sizes="72x72" href="/favicon/apple-touch-icon-72x72.png" />
      <link rel="apple-touch-icon-precomposed" sizes="144x144" href="/favicon/apple-touch-icon-144x144.png" />
      <link rel="apple-touch-icon-precomposed" sizes="60x60" href="/favicon/apple-touch-icon-60x60.png" />
      <link rel="apple-touch-icon-precomposed" sizes="120x120" href="/favicon/apple-touch-icon-120x120.png" />
      <link rel="apple-touch-icon-precomposed" sizes="76x76" href="/favicon/apple-touch-icon-76x76.png" />
      <link rel="apple-touch-icon-precomposed" sizes="152x152" href="/favicon/apple-touch-icon-152x152.png" />
      <link rel="icon" type="image/png" href="/favicon/favicon-196x196.png" sizes="196x196" />
      <link rel="icon" type="image/png" href="/favicon/favicon-96x96.png" sizes="96x96" />
      <link rel="icon" type="image/png" href="/favicon/favicon-32x32.png" sizes="32x32" />
      <link rel="icon" type="image/png" href="/favicon/favicon-16x16.png" sizes="16x16" />
      <link rel="icon" type="image/png" href="/favicon/favicon-128.png" sizes="128x128" />

      {/* Microsoft Tiles */}
      <meta name="application-name" content={siteName} />
      <meta name="msapplication-TileColor" content="#FFFFFF" />
      <meta name="msapplication-TileImage" content="/favicon/mstile-144x144.png" />
      <meta name="msapplication-square70x70logo" content="/favicon/mstile-70x70.png" />
      <meta name="msapplication-square150x150logo" content="/favicon/mstile-150x150.png" />
      <meta name="msapplication-wide310x150logo" content="/favicon/mstile-310x150.png" />
      <meta name="msapplication-square310x310logo" content="/favicon/mstile-310x310.png" />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:site_name" content={siteName} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
};

export default Seo;
